import { EmailBlock, EmailBlockKind, TemplateVariable } from '../types/communication';

const BRAND = '#00B6AD';
const INK = '#111827';
const MUTED = '#6B7280';

export const EMAIL_BLOCK_LABELS: Record<EmailBlockKind, string> = {
    TEXT: 'Texto',
    BUTTON: 'Botão',
    FOOTER: 'Rodapé',
};

export const EMAIL_BLOCK_HINTS: Record<EmailBlockKind, string> = {
    TEXT: 'Texto livre com formatação, listas, links e imagens',
    BUTTON: 'Chamada para ação com link',
    FOOTER: 'Assinatura e avisos legais',
};

let blockSequence = 0;

export const newBlockId = (): string => {
    blockSequence += 1;
    return `blk-${Date.now().toString(36)}-${blockSequence}`;
};

export const createBlock = (kind: EmailBlockKind): EmailBlock => {
    const id = newBlockId();
    switch (kind) {
        case 'TEXT':
            return { id, kind, text: '<p>Olá,</p><p>Escreva aqui a mensagem. Use "Variável" para inserir dados de cada envio.</p>' };
        case 'BUTTON':
            return { id, kind, text: 'Acessar', url: 'https://', align: 'left' };
        default:
            return { id, kind, text: 'Agrolend · Este é um e-mail automático, não responda.' };
    }
};

const escapeHtml = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const safeUrl = (url?: string): string => {
    const value = (url ?? '').trim();
    if (!value) return '#';
    if (/^(https?:|mailto:)/i.test(value)) return value;
    if (value.startsWith('{{') || value.startsWith('/') || value.startsWith('#')) return value;
    return '#';
};

const paragraphs = (text = ''): string =>
    text
        .split(/\n{2,}/)
        .map((p) => escapeHtml(p).replace(/\n/g, '<br/>'))
        .map((p) => `<p style="margin:0 0 12px 0;">${p}</p>`)
        .join('');

const blockHtml = (block: EmailBlock): string => {
    switch (block.kind) {
        case 'TEXT':
            return `<div style="font-size:15px;line-height:1.6;color:${INK};">${block.text ?? ''}</div>`;
        case 'BUTTON':
            return `<div style="text-align:${block.align ?? 'left'};margin:8px 0 20px 0;"><a href="${escapeHtml(safeUrl(block.url))}" style="display:inline-block;background:${BRAND};color:#ffffff;text-decoration:none;font-weight:600;font-size:14px;padding:12px 22px;border-radius:8px;">${escapeHtml(block.text ?? '')}</a></div>`;
        default:
            return `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #E5E7EB;font-size:12px;line-height:1.5;color:${MUTED};">${paragraphs(block.text)}</div>`;
    }
};

export const blocksToHtml = (blocks: EmailBlock[]): string =>
    `<div style="background:#F3F4F6;padding:24px 0;font-family:Inter,Helvetica,Arial,sans-serif;"><div style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;"><div style="height:6px;background:${BRAND};"></div><div style="padding:28px 32px;">${blocks
        .map(blockHtml)
        .join('')}</div></div></div>`;

export const blockText = (block: EmailBlock): string => [block.text ?? '', block.url ?? ''].join('\n');

export const htmlBodyContent = (html: string): string => {
    const body = /<body[^>]*>([\s\S]*)<\/body>/i.exec(html);
    return (body ? body[1] : html)
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .trim();
};

export const looksLikeLayoutHtml = (html: string): boolean => /<table|\{%/i.test(html);

const isEnvelopeOnly = (v: TemplateVariable) => v.format === 'EMAIL' || v.format === 'PHONE' || /CC|BCC/i.test(v.name);

export const defaultEmailBlocks = (variables: TemplateVariable[]): EmailBlock[] => {
    const fileVariable = variables.find((v) => v.format === 'FILE_URL');
    const nameVariable = variables.find((v) => /NAME/i.test(v.name) && /FULL|DESTIN/i.test(`${v.name} ${v.label}`));
    const details = variables.filter((v) => v !== fileVariable && v !== nameVariable && !isEnvelopeOnly(v));
    const list = details.map((v) => `<li><strong>${v.label}:</strong> {{${v.name}}}</li>`).join('');
    const blocks: EmailBlock[] = [
        {
            id: newBlockId(),
            kind: 'TEXT',
            text: `<p>Olá${nameVariable ? ` {{${nameVariable.name}}}` : ''},</p><p>Segue abaixo o resumo desta comunicação.</p>${list ? `<ul>${list}</ul>` : ''}`,
        },
    ];
    if (fileVariable) {
        blocks.push({ id: newBlockId(), kind: 'BUTTON', text: 'Baixar documento', url: `{{${fileVariable.name}}}`, align: 'left' });
    }
    blocks.push({
        id: newBlockId(),
        kind: 'FOOTER',
        text: 'Em caso de dúvidas, fale com a nossa equipe pelo cobranca@agrolend.agr.br.\nAgrolend Sociedade de Crédito Direto S.A.',
    });
    return blocks;
};
